import { type ColumnDef } from "components/table";
import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { InfoTooltip } from "components/InfoTooltip/InfoTooltip";
import { Icon } from "components/icon/Icon";
import { TFunction } from "i18next";
import {
  FirestoreProductSubject,
  type KeywordFeedbackDetails,
  SubjectSource,
} from "reedy-data/api";
import { Skeleton } from "components/skeleton";
import { Badge } from "components/ui/badge";
import { Button } from "components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { compareHotnessScore } from "../../utils/keywordUtils";
import type { KeywordFeedbackEntry } from "../../hooks/useKeywordFeedback";
import { KeywordFeedbackPopover } from "../KeywordFeedbackPopover";

interface ColumnsOptions {
  isScoring: boolean;
  hasGeneratedKeywords: boolean;
  showScoring: boolean;
  showScorePlaceholder: boolean;
  showReasoning: boolean;
}

interface ColumnContext {
  matchingWords?: string[];
  isInFinal?: (subject: FirestoreProductSubject) => boolean;
  onToggle?: (subject: FirestoreProductSubject) => void;
  getFeedbackEntry?: (keyword: string) => KeywordFeedbackEntry | undefined;
  isFeedbackOpen?: (keyword: string) => boolean;
  onFeedbackOpenChange?: (keyword: string, open: boolean) => void;
  onFeedbackSubmit?: (
    keyword: string,
    details?: KeywordFeedbackDetails,
  ) => Promise<void>;
  onFeedbackDiscard?: (keyword: string) => Promise<void>;
}

const scoreColumn = (
  t: TFunction,
  options: ColumnsOptions,
): ColumnDef<FirestoreProductSubject> => ({
  accessorKey: "hotnessScore",
  size: 300,
  header: ({ column }) => (
    <DataTableColumnHeader
      column={column}
      title={t("components:keywordsTab.unifiedKeywordsTable.columns.score")}
      className="justify-center"
      prefix={
        <InfoTooltip
          text={t("components:keywordsTab.unifiedKeywordsTable.scoreTooltip")}
          side="top"
          iconSize="small"
        />
      }
    />
  ),
  cell: ({ row }) => {
    if (options.isScoring) {
      return (
        <div className="flex justify-center">
          <Skeleton
            className="h-4 w-full"
            data-testid="score-loading-skeleton"
          />
        </div>
      );
    }

    if (!options.hasGeneratedKeywords) {
      if (!options.showScorePlaceholder) {
        return null;
      }
      return (
        <div className="flex justify-center bg-secondary-25 -mx-4 -my-8 p-4">
          <Badge
            variant="secondary"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {t("components:keywordsTab.unifiedKeywordsTable.scorePlaceholder")}
          </Badge>
        </div>
      );
    }

    const score = row.original.hotnessScore;
    return (
      <div className="text-center">
        {score == null ? "-" : Math.round(score)}
      </div>
    );
  },
  sortFn: (rowA, rowB) => {
    return compareHotnessScore(rowA.original, rowB.original);
  },
});

export const columns = (
  t: TFunction,
  options: ColumnsOptions,
): ColumnDef<FirestoreProductSubject>[] => {
  const baseColumns: ColumnDef<FirestoreProductSubject>[] = [
    {
      accessorKey: "subjectHeadingText",
      enableSorting: false,
      header: () =>
        t("components:keywordsTab.unifiedKeywordsTable.columns.keywordName"),
      cell: ({ row, ...context }) => {
        const {
          matchingWords = [],
          isInFinal,
          onToggle,
          getFeedbackEntry,
          isFeedbackOpen,
          onFeedbackOpenChange,
          onFeedbackSubmit,
          onFeedbackDiscard,
        } = context as ColumnContext;

        const subject = row.original;
        const text = row.getValue("subjectHeadingText") as string;
        const parts = text.split(/([^\p{L}\p{N}]+)/u);
        const inFinal = isInFinal?.(subject) ?? false;
        const tooltip = inFinal
          ? t(
              "components:keywordsTab.unifiedKeywordsTable.actions.removeFromFinal",
            )
          : t("components:keywordsTab.unifiedKeywordsTable.actions.addToFinal");

        const reasoningText = subject.reasoning?.trim() ?? "";
        const isDatabaseKeyword = subject.source === SubjectSource.DATABASE;
        const showReasoningRow =
          options.showReasoning &&
          !isDatabaseKeyword &&
          reasoningText.length > 0;
        // Feedback only applies to generated suggestions — database keywords
        // come from the curated source and don't need user feedback.
        const showFeedback =
          !isDatabaseKeyword &&
          text.length > 0 &&
          getFeedbackEntry !== undefined &&
          isFeedbackOpen !== undefined &&
          onFeedbackOpenChange !== undefined &&
          onFeedbackSubmit !== undefined &&
          onFeedbackDiscard !== undefined;

        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => onToggle?.(subject)}
                    disabled={!onToggle}
                    aria-label={tooltip}
                    testId={`toggle-keyword-${text}`}
                    className="inline-flex w-fit text-left p-0 rounded-none text-inherit hover:text-inherit"
                  >
                    <Icon
                      name={inFinal ? "circle-check" : "circle-plus"}
                      size="medium"
                      iconClassName={
                        inFinal ? "text-primary-600" : "text-secondary-400"
                      }
                      data-testid={
                        inFinal
                          ? `keyword-added-check-${text}`
                          : `keyword-add-icon-${text}`
                      }
                    />
                    <span
                      className="font-medium"
                      data-testid={`keyword-${text}`}
                    >
                      {parts.map((part, index) => {
                        const isMatch = matchingWords.some(
                          (word) => part.toLowerCase() === word.toLowerCase(),
                        );
                        return (
                          <span
                            // biome-ignore lint/suspicious/noArrayIndexKey: parts come from splitting one string and never reorder
                            key={`${index}-${part}`}
                            className={
                              isMatch ? "font-bold text-primary-600 block" : ""
                            }
                          >
                            {part}
                          </span>
                        );
                      })}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
              {showFeedback && (
                <KeywordFeedbackPopover
                  keyword={text}
                  entry={getFeedbackEntry(text)}
                  isOpen={isFeedbackOpen(text)}
                  onOpenChange={(open) => onFeedbackOpenChange(text, open)}
                  onSubmit={(details) => onFeedbackSubmit(text, details)}
                  onDiscard={() => onFeedbackDiscard(text)}
                />
              )}
            </div>
            {showReasoningRow && (
              <p
                className="text-xs italic text-secondary-500 leading-snug pl-6 whitespace-pre-line break-words"
                data-testid={`keyword-reasoning-${text}`}
              >
                {reasoningText}
              </p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "source",
      size: 300,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "components:keywordsTab.unifiedKeywordsTable.columns.database",
          )}
          className="justify-center"
        />
      ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          {row.original.source === SubjectSource.DATABASE ? (
            <Icon
              name="check"
              size="small"
              className="text-success-500"
              data-testid="database-keyword-check"
            />
          ) : null}
        </div>
      ),
      sortFn: (rowA, rowB) => {
        const aIsDatabase =
          rowA.original.source === SubjectSource.DATABASE ? 1 : 0;
        const bIsDatabase =
          rowB.original.source === SubjectSource.DATABASE ? 1 : 0;
        return bIsDatabase - aIsDatabase;
      },
    },
  ];

  if (options.showScoring) {
    baseColumns.push(scoreColumn(t, options));
  }

  return baseColumns;
};
